import { takeLatest, put, call, select } from 'redux-saga/effects';
import { message } from 'antd';
import * as actions from './actions';
import * as types from './types';
import {
  getSPIThresholds,
  saveSelectedKPI,
  updateCustomKPI,
  deleteCustomKPI,
  saveAndApplySelectedKPI,
} from '@src/api/SPI';
import { selectedKPIConfigSelector } from '@store/overviewDashboard/base/selectors';
import { thresholdConfigsSelector, selectedThresholdConfigSelector } from './selectors';
import { setSelectedKPIId } from '../base/actions';
import { DEFAULT_KPI_ID } from '@src/utils/kpi';
import { clearCache } from '@src/api/cacheAdapter/config';

function* fetching() {
  try {
    yield put(actions.setInitializing({ isInitializing: true }));
    yield put(actions.setFetching({ isFetching: true }));
    const response = yield call(getSPIThresholds, {});
    const { data } = response;
    yield put(actions.setSPIThresholds(data));
    const selectedKpi = yield select(selectedKPIConfigSelector);
    const config =
      selectedKpi && data.kpiThresholds.find((item) => item.kpiID === selectedKpi.kpiID);
    const defaultConfig = data.kpiThresholds.find((item) => item.kpiID === DEFAULT_KPI_ID);
    if (config) {
      yield put(actions.setSelectedKPIConfig(config));
    } else {
      yield put(actions.setSelectedKPIConfig(defaultConfig));
      yield put(setSelectedKPIId(defaultConfig));
    }
    yield put(actions.setFetching({ isFetching: false }));
    yield put(actions.setInitializing({ isInitializing: false }));

    yield put(actions.setError({ errorMsg: '', errorCode: 0 }));
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error!';
    const errorCode = error.code || '';
    message.error(errorMsg);
    yield put(actions.setError({ errorMsg, errorCode }));
    yield put(actions.setFetching({ isFetching: false }));
  }
}

function* saveAndApplyKPI(actionPayload) {
  try {
    yield put(actions.setFetching({ isFetching: true }));
    const response = yield call(saveAndApplySelectedKPI, actionPayload.payload);
    const { data } = response;
    const kpiConfig = { ...actionPayload.payload, kpiID: data.kpiID };
    yield put(setSelectedKPIId(kpiConfig));
    yield put(actions.setSelectedKPIConfig(kpiConfig));
    actionPayload.closePopup();
    const thresholdsList = yield select(thresholdConfigsSelector);
    thresholdsList.push(kpiConfig);
    yield put(actions.setSPIThresholds({ kpiThresholds: thresholdsList }));
    yield put(actions.setFetching({ isFetching: false }));
    yield put(actions.setError({ errorMsg: '', errorCode: 0 }));
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error!';
    const errorCode = error.code || '';
    message.error(errorMsg);
    yield put(actions.setError({ errorMsg, errorCode }));
    yield put(actions.setFetching({ isFetching: false }));
  }
}

function* saveUserKpiPrerefernce(actionPayload) {
  try {
    yield put(actions.setFetching({ isFetching: true }));
    const { shouldEdit } = actionPayload;

    if (shouldEdit) {
      yield call(updateCustomKPI, actionPayload.payload);
      let thresholdsList = yield select(thresholdConfigsSelector);
      thresholdsList = thresholdsList.map((item) =>
        item.kpiID === actionPayload.payload.kpiID ? actionPayload.payload : item,
      );
      yield put(actions.setSPIThresholds({ kpiThresholds: thresholdsList }));
    }

    const kpiId = actionPayload.payload.kpiID;
    const response = yield call(saveSelectedKPI, { kpiId });
    // clear axios cache as KPI changed
    clearCache();
    yield put(actions.setSelectedKPIConfig(actionPayload.payload));
    yield put(setSelectedKPIId(actionPayload.payload));
    actionPayload.closePopup();
    yield put(actions.setFetching({ isFetching: false }));
    yield put(actions.setError({ errorMsg: '', errorCode: 0 }));
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error!';
    const errorCode = error.code || '';
    message.error(errorMsg);
    yield put(actions.setError({ errorMsg, errorCode }));
    yield put(actions.setFetching({ isFetching: false }));
  }
}

function* deleteKPI(actionPayload) {
  try {
    yield put(actions.setFetching({ isFetching: true }));
    yield call(deleteCustomKPI, actionPayload.payload);

    const selectedConfig = yield select(selectedThresholdConfigSelector);
    const selectedKpi = yield select(selectedKPIConfigSelector);
    let thresholdsList = yield select(thresholdConfigsSelector);

    if (selectedKpi.kpiID === selectedConfig.kpiID) {
      const config = thresholdsList.find((item) => item.kpiID === DEFAULT_KPI_ID);
      yield put(actions.setSelectedKPIConfig(config));
      yield put(setSelectedKPIId(config));
    } else {
      const config = thresholdsList.find((item) => item.kpiID === selectedKpi.kpiID);
      yield put(actions.setSelectedKPIConfig(config));
      yield put(setSelectedKPIId(config));
    }

    if (actionPayload.closePopup) {
      actionPayload.closePopup();
    }
    thresholdsList = thresholdsList.filter((item) => item.kpiID !== actionPayload.payload.kpiId);
    yield put(actions.setSPIThresholds({ kpiThresholds: thresholdsList }));
    yield put(actions.setFetching({ isFetching: false }));
    yield put(actions.setError({ errorMsg: '', errorCode: 0 }));
  } catch (error: any) {
    const errorMsg = error.message || 'Unknown error!';
    const errorCode = error.code || '';
    message.error(errorMsg);
    yield put(actions.setError({ errorMsg, errorCode }));
    yield put(actions.setFetching({ isFetching: false }));
  }
}

function* initRequest() {
  yield put(actions.init());
}

function* watchInitRequest() {
  yield takeLatest([types.INIT], initRequest);
}

function* watchFetching() {
  yield takeLatest(types.FETCH_SPI_THRESHOLD, fetching);
}

function* watchSaving() {
  yield takeLatest(types.SAVE_USER_KPI_PREFERENCE, saveUserKpiPrerefernce);
}

function* watchDeleteKPI() {
  yield takeLatest(types.DELETE_CUSTOM_KPI, deleteKPI);
}

function* watchSaveAndApply() {
  yield takeLatest(types.SAVE_AND_APPLY, saveAndApplyKPI);
}

// function* watchSelectedKPI() {
//   yield takeLatest(baseTypes.SET_SELECTED_KPI_CONFIG, setSelectedConfig);
// }

export default [watchInitRequest, watchFetching, watchSaving, watchDeleteKPI, watchSaveAndApply];
